import React from 'react';
import { Link } from 'react-router-dom';
import { MeetingDocument } from '../services/legistarApi';

interface MeetingCardProps {
  id: number;
  /** Fallback heading ("<Committee> - <Month Year>") when no hearing topic exists */
  title: string;
  date: string;
  /** Raw ISO EventDate, used for the Upcoming badge */
  rawDate?: string;
  time: string;
  location: string;
  committee?: string;
  /** Legistar event comment, e.g. "Jointly with the Committee on Finance" or "VOTE*" */
  comment?: string | null;
  /** Scannable hearing subjects (oversight titles, budget hearings) */
  topics?: string[];
  documents: MeetingDocument[];
}

const MeetingCard: React.FC<MeetingCardProps> = ({
  id,
  title,
  date,
  rawDate,
  time,
  location,
  committee,
  comment,
  topics = [],
  documents,
}) => {
  // The headline a staffer scans for: the hearing subject when one exists,
  // otherwise the committee-month fallback.
  const headline = topics.length > 0 ? topics[0] : title;
  const extraTopics = topics.slice(1);

  const isUpcoming = rawDate
    ? new Date(rawDate).getTime() >= new Date(new Date().toDateString()).getTime()
    : false;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6 hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-2xl font-bold text-nyc-blue">
          <Link to={`/event/${id}`} className="hover:underline">
            {headline}
          </Link>
        </h2>
        {isUpcoming && (
          <span className="flex-shrink-0 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Upcoming
          </span>
        )}
      </div>
      {extraTopics.map((topic) => (
        <p key={topic} className="text-lg font-semibold text-nyc-blue/80">{topic}</p>
      ))}
      {committee && topics.length > 0 && (
        <h3 className="text-sm font-medium text-gray-600 mt-1">{committee}</h3>
      )}
      {comment && <p className="text-sm italic text-gray-500 mt-1">{comment}</p>}

      <div className="grid grid-cols-2 gap-4 my-4">
        <div>
          <p className="text-gray-600">Date:</p>
          <p className="font-semibold">{date}</p>
        </div>
        <div>
          <p className="text-gray-600">Time:</p>
          <p className="font-semibold">{time}</p>
        </div>
        <div>
          <p className="text-gray-600">Location:</p>
          <p className="font-semibold">{location}</p>
        </div>
      </div>

      {documents.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-nyc-blue mb-2">On the agenda:</h3>
          <ul className="space-y-2">
            {documents.map((doc, index) => (
              <li key={index}>
                {doc.MatterId ? (
                  // Our own bill page — richer than the Legistar deep link
                  <Link to={`/legislation/${doc.MatterId}`} className="text-nyc-orange hover:underline">
                    {doc.name}
                  </Link>
                ) : (
                  <a
                    href={doc.url}
                    className="text-nyc-orange hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {doc.name}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-gray-100">
        <Link to={`/event/${id}`} className="text-sm font-medium text-nyc-blue hover:underline">
          View meeting details &rarr;
        </Link>
      </div>
    </div>
  );
};

export default MeetingCard;
